import { Facet } from '@coveo/headless';
import {
  DistanceOption,
  LocationSearchFacetControllers,
  LocationSearchFacetKey,
  SearchLocationFields,
  SearchLocationFilterField,
} from './LocationSearch.types';
import { SitesHeadlessFacets } from 'lib/utils/coveo/sitesHeadless/sitesHeadlessFacets';

/**
 * Sitecore Multilist fields may arrive as a raw item array, or wrapped as
 * `{ value }`, `{ jsonValue }`, or `{ targetItems }` depending on Layout Service vs GraphQL.
 */
export const toSitecoreItemList = <T>(field: unknown): T[] => {
  if (Array.isArray(field)) {
    return field as T[];
  }

  if (!field || typeof field !== 'object') {
    return [];
  }

  const wrapped = field as { value?: unknown; jsonValue?: unknown; targetItems?: unknown };
  if (Array.isArray(wrapped.value)) {
    return wrapped.value as T[];
  }
  if (Array.isArray(wrapped.jsonValue)) {
    return wrapped.jsonValue as T[];
  }
  if (Array.isArray(wrapped.targetItems)) {
    return wrapped.targetItems as T[];
  }

  return [];
};

/** Like `toSitecoreItemList`, but also accepts a single resolved Droplink item. */
export const toSitecoreItemOrList = <T>(field: unknown): T[] => {
  const list = toSitecoreItemList<T>(field);
  if (list.length > 0) {
    return list;
  }

  if (!field || typeof field !== 'object') {
    return [];
  }

  if ('fields' in field) {
    return [field as T];
  }

  const wrapped = field as { value?: unknown; jsonValue?: unknown };
  const single = wrapped.value ?? wrapped.jsonValue;
  if (single && typeof single === 'object' && 'fields' in single) {
    return [single as T];
  }

  return [];
};

export const LOCATION_SEARCH_FILTER_KEYS = {
  DISTANCE: 'distance',
  CONDITION: 'condition',
  STATUS: 'status',
  AGE_GROUP: 'ageGroup',
  LANGUAGE: 'language',
} as const;

export type LocationSearchFilterKey =
  (typeof LOCATION_SEARCH_FILTER_KEYS)[keyof typeof LOCATION_SEARCH_FILTER_KEYS];

const FACET_LABEL_ALIASES: Record<LocationSearchFacetKey, string[]> = {
  condition: ['condition'],
  status: ['status'],
  ageGroup: ['adults', 'age group', 'agegroup'],
  language: ['language'],
};

export const resolveFilterLabel = (item: SearchLocationFilterField): string =>
  item.fields?.distanceFilterLabel?.value?.trim() ||
  item.fields?.filterLabel?.value?.trim() ||
  item.displayName?.trim() ||
  item.name?.trim() ||
  '';

export const isFilterVisible = (item: SearchLocationFilterField): boolean =>
  Boolean(resolveFilterLabel(item));

export const resolveDistanceOptionLabel = (option: DistanceOption): string =>
  option.fields?.label?.value?.trim() ||
  option.fields?.distanceFilterValue?.value?.trim() ||
  option.fields?.value?.value?.trim() ||
  option.displayName?.trim() ||
  option.name?.trim() ||
  '';

/** Parses "25 miles" / "25 mi" / "Up to 25 miles" → 25; "Any Distance" → undefined (no radius). */
export const parseDistanceMiles = (label: string | null | undefined): number | undefined => {
  if (!label?.trim()) {
    return undefined;
  }

  const normalized = label.trim().toLowerCase();
  if (normalized.includes('any')) {
    return undefined;
  }

  const match = normalized.match(/(\d+(?:\.\d+)?)/);
  if (!match) {
    return undefined;
  }

  const miles = Number(match[1]);
  return Number.isFinite(miles) ? miles : undefined;
};

export interface DistanceFilterMenuOption {
  id: string;
  label: string;
  /** Radius in miles from Sitecore maxDistance; undefined for "Any distance". */
  miles?: number;
}

const resolveDistanceOptionMiles = (option: DistanceOption, label: string): number | undefined => {
  const maxDistance = option.fields?.maxDistance?.value;
  if (typeof maxDistance === 'number' && Number.isFinite(maxDistance)) {
    return maxDistance;
  }
  return parseDistanceMiles(label);
};

export interface DistanceFilterSelection {
  label: string;
  miles?: number;
}

/** Maps a Sitecore distance options multilist into radio menu options. */
export const getDistanceFilterOptions = (rawItems: unknown): DistanceFilterMenuOption[] =>
  toSitecoreItemList<DistanceOption>(rawItems).reduce<DistanceFilterMenuOption[]>(
    (acc, option, index) => {
      const label = resolveDistanceOptionLabel(option);
      if (!label) {
        return acc;
      }

      acc.push({
        id: option.id || `distance-${index}`,
        label,
        miles: resolveDistanceOptionMiles(option, label),
      });
      return acc;
    },
    []
  );

/**
 * Resolves distance filter menu options from SearchLocation root `distanceFilterItems`,
 * then nested Distance filter item sources. `defaultDistance` is excluded — it is a
 * single-item Droplink used only for the Closest badge threshold.
 */
export const resolveDistanceFilterItemsSource = (
  rootFields: Pick<SearchLocationFields, 'distanceFilterItems'> | undefined,
  distanceFilterItem: SearchLocationFilterField | undefined
): unknown =>
  rootFields?.distanceFilterItems ??
  distanceFilterItem?.distanceFilterItems ??
  distanceFilterItem?.fields?.distanceFilterItems;

/** Resolves all Distance filter menu options from SearchLocation fields. */
export const getLocationSearchDistanceOptions = (
  fields?: SearchLocationFields
): DistanceFilterMenuOption[] => {
  const filterFields = fields?.filterFields;
  const visibleFilters = sortFiltersWithDistanceFirst(
    toSitecoreItemList<SearchLocationFilterField>(filterFields).filter(isFilterVisible)
  );
  const distanceFilterItem = visibleFilters.find(
    (item) => resolveFilterKey(item) === LOCATION_SEARCH_FILTER_KEYS.DISTANCE
  );

  return getDistanceFilterOptions(resolveDistanceFilterItemsSource(fields, distanceFilterItem));
};

/**
 * Exact match of the profile distance against the Sitecore option `key`, nothing else.
 * Returns the menu option because the filter bar shows `label`, which can differ from `key`.
 */
export const findProfileDistanceOption = (
  profileDistance: string | null | undefined,
  fields?: SearchLocationFields
): DistanceFilterMenuOption | null => {
  const key = profileDistance?.trim();
  if (!key) {
    return null;
  }

  const distanceFilterItem = toSitecoreItemList<SearchLocationFilterField>(fields?.filterFields)
    .filter(isFilterVisible)
    .find((item) => resolveFilterKey(item) === LOCATION_SEARCH_FILTER_KEYS.DISTANCE);
  const items = toSitecoreItemList<DistanceOption>(
    resolveDistanceFilterItemsSource(fields, distanceFilterItem)
  );
  const match = items.find((item) => item.fields?.key?.value?.trim() === key);

  return getDistanceFilterOptions(items).find((option) => option.id === match?.id) ?? null;
};

/**
 * Maps the CMS `defaultDistance` droplink to a menu selection, matching against
 * `distanceFilterItems` options by id, then label, then miles.
 */
export const resolveDefaultDistanceSelection = (
  defaultDistance: unknown,
  distanceOptions: DistanceFilterMenuOption[]
): DistanceFilterSelection | null => {
  const defaultItems = toSitecoreItemOrList<DistanceOption>(defaultDistance);
  if (defaultItems.length === 0) {
    return null;
  }

  const defaultItem = defaultItems[0];
  const defaultId = defaultItem.id?.trim();
  const defaultLabel = resolveDistanceOptionLabel(defaultItem);
  const defaultMiles = resolveDistanceOptionMiles(defaultItem, defaultLabel);

  if (!defaultLabel) {
    return null;
  }

  if (defaultId) {
    const byId = distanceOptions.find((option) => option.id === defaultId);
    if (byId) {
      return { label: byId.label, miles: byId.miles };
    }
  }

  const byLabel = distanceOptions.find((option) => option.label === defaultLabel);
  if (byLabel) {
    return { label: byLabel.label, miles: byLabel.miles };
  }

  if (defaultMiles != null) {
    const byMiles = distanceOptions.find((option) => option.miles === defaultMiles);
    if (byMiles) {
      return { label: byMiles.label, miles: byMiles.miles };
    }
  }

  return { label: defaultLabel, miles: defaultMiles };
};

/** Shortens "25 miles" → "25 mi" for the active pill label. */
export const formatDistancePillValue = (label: string): string => {
  const miles = parseDistanceMiles(label);
  if (miles == null) {
    return label.trim();
  }
  return `${miles} mi`;
};

export const resolveFilterKey = (
  item: SearchLocationFilterField
): LocationSearchFilterKey | null => {
  const label = resolveFilterLabel(item).toLowerCase();
  const name = (item.name || item.displayName || '').trim().toLowerCase();

  if (
    label === 'distance' ||
    name === 'distance' ||
    Boolean(item.fields?.distanceFilterLabel?.value?.trim())
  ) {
    return LOCATION_SEARCH_FILTER_KEYS.DISTANCE;
  }

  const candidates = [label, name].filter(Boolean);

  for (const [facetKey, aliases] of Object.entries(FACET_LABEL_ALIASES) as [
    LocationSearchFacetKey,
    string[],
  ][]) {
    if (candidates.some((candidate) => aliases.includes(candidate))) {
      return facetKey;
    }
  }

  return null;
};

/** Keeps Sitecore order for non-distance filters, but always places Distance first. */
export const sortFiltersWithDistanceFirst = (
  items: SearchLocationFilterField[]
): SearchLocationFilterField[] => {
  const distanceFilters: SearchLocationFilterField[] = [];
  const otherFilters: SearchLocationFilterField[] = [];

  items.forEach((item) => {
    if (resolveFilterKey(item) === LOCATION_SEARCH_FILTER_KEYS.DISTANCE) {
      distanceFilters.push(item);
    } else {
      otherFilters.push(item);
    }
  });

  return [...distanceFilters, ...otherFilters];
};

export const mapSitesFacetsToControllers = (
  facets: SitesHeadlessFacets
): LocationSearchFacetControllers => ({
  condition: facets.siteRelatedConditionsFacet,
  status: facets.siteStatusFacet,
  ageGroup: facets.siteRecruitingTypeFacet,
  language: facets.siteLanguagesFacet,
});

export const getFacetController = (
  controllers: LocationSearchFacetControllers | undefined,
  key: LocationSearchFacetKey
): Facet | undefined => controllers?.[key];

export const getSelectedFacetValues = (facet: Facet | undefined): string[] => {
  if (!facet) {
    return [];
  }
  return facet.state.values
    .filter((value) => facet.isValueSelected(value))
    .map((value) => value.value);
};

export const buildStagedSelectionFromFacet = (
  facet: Facet | undefined
): Record<string, boolean> => {
  if (!facet) {
    return {};
  }

  return facet.state.values.reduce<Record<string, boolean>>((acc, value) => {
    acc[value.value] = facet.isValueSelected(value);
    return acc;
  }, {});
};
